import { v4 as uuidv4 } from 'uuid';
import { dateToAgeMonths, ageToMonths } from './dateUtil';

//Total units allowed per child across all plan rows
export const TOTAL_UNITS_PER_CHILD = 10;

//Generate a new empty plan row
export const newPlanRow = () => ({ rowId: uuidv4(), plan: null, units: 0 });

//Availability predicate - DB driven min/max age in months.
//min inclusive, max EXCLUSIVE: a child exactly at the bracket top belongs to the next plan.
export const planFitsAge = (plan, ageMonths) =>
    ageMonths !== null && ageMonths !== undefined && !!plan &&
    ageMonths >= (plan.minAgeMonths ?? 0) &&
    (plan.maxAgeMonths === null || plan.maxAgeMonths === undefined || ageMonths < plan.maxAgeMonths);

//Available plans for a child age (months), excluding family plans & already selected routes
export const getAvailablePlans = (plans, ageMonths, excludeRoutes = []) =>
    plans?.filter(plan => !plan?.familyPlan && planFitsAge(plan, ageMonths) && !excludeRoutes.includes(plan.route)) ?? [];

//Child age in months - precise birthday wins, else age selector value
export const kidAgeMonths = (kid) =>
    (kid?.preBorn) ? 0 :
        (kid?.birthday) ? dateToAgeMonths(kid.birthday) :
            (kid?.age !== null && kid?.age !== undefined) ? ageToMonths(kid.age) : null;

//Total units across all the child plan rows
export const kidTotalUnits = (kid) =>
    kid?.plans?.reduce((sum, row) => sum + (Number(row.units) || 0), 0) ?? 0;

//Max units for a row = min(plan own cap, 10 - units in other rows)
export const rowMaxUnits = (kid, rowId, plan) => {
    const otherRowsUnits = kid?.plans?.filter(row => row.rowId !== rowId)
        .reduce((sum, row) => sum + (Number(row.units) || 0), 0) ?? 0;
    const remaining = Math.max(0, TOTAL_UNITS_PER_CHILD - otherRowsUnits);
    return (plan?.units) ? Math.min(plan.units, remaining) : remaining;
};

//Routes already selected on the child rows (optionally excluding one row)
export const kidSelectedRoutes = (kid, excludeRowId = null) =>
    kid?.plans?.filter(row => row.plan && row.rowId !== excludeRowId).map(row => row.plan.route) ?? [];

export const kidHasCompleteRow = (kid) => !!kid?.plans?.some(row => row.plan && row.units > 0);

export const kidRowsAllValid = (kid) => !!kid?.plans?.length && kid.plans.every(row => row.plan && row.units > 0);

//Can the child add another plan row? total units < 10 AND an unselected plan fits the age
export const kidCanAddPlan = (kid, plans) =>
    kidTotalUnits(kid) < TOTAL_UNITS_PER_CHILD &&
    getAvailablePlans(plans, kidAgeMonths(kid), kidSelectedRoutes(kid)).length > 0;

//Migrate legacy single plan kid shape {plan, units} -> {plans: [{rowId, plan, units}]}
export const normalizeKid = (kid) => {
    if (kid?.plans) return kid;
    const { plan, units, requestLine, ...rest } = kid ?? {};
    return { ...rest, plans: [{ rowId: uuidv4(), plan: plan ?? null, units: units ?? 0, ...(requestLine ? { requestLine } : {}) }] };
};

//Re-resolve stored/stale plan objects by route so they carry fresh DB fields (minAgeMonths/maxAgeMonths)
//Plans that no longer exist on the simulator are cleared from the row
export const resolveKidPlans = (kid, plans) => ({
    ...kid,
    plans: kid.plans?.map(row => {
        if (!row.plan) return row;
        const freshPlan = plans?.find(plan => plan.route === row.plan.route);
        return (freshPlan) ? { ...row, plan: freshPlan } : { ...row, plan: null, units: 0 };
    }) ?? [newPlanRow()],
});

//True when a kid still holds a legacy shape or stale plan objects (missing fresh DB fields)
export const kidNeedsPlanResolve = (kid) =>
    !kid?.plans || kid.plans.some(row => row.plan && row.plan.minAgeMonths === undefined);

//Distinct children count over flattened completedKids entries (one entry per kid plan row)
export const distinctKidsCount = (completedKids) =>
    new Set(completedKids?.map(kid => kid.kidId ?? kid.id)).size;

//Continue lead - group flat SF unit request lines back into kids with plan rows
export const groupLinesToKids = (lines, plans, baseKid = {}) => {
    const kidsByKey = new Map();
    lines?.forEach(line => {
        const key = line.personId || line.passportId || `${line.name}|${line.birthday}|${line.preBorn}`;
        if (!kidsByKey.has(key)) {
            const { plan, units, requestLine, ...kidFields } = line;
            kidsByKey.set(key, { ...baseKid, id: uuidv4(), ...kidFields, plans: [] });
        }
        const kid = kidsByKey.get(key);
        kid.plans.push({
            rowId: uuidv4(),
            plan: (line.plan || line.plan === 0) ? plans?.find(plan => plan.route === line.plan) ?? null : null,
            units: line.units ?? 0,
            requestLine: line.requestLine,
        });
    });
    return [...kidsByKey.values()].map(kid => (kid.plans.length) ? kid : { ...kid, plans: [newPlanRow()] });
};
